import { useTranslation } from 'react-i18next';
import { ApiAuthChallengeRequired } from '../../../openapi';
import { useMemo } from 'react';
import { styled, Typography } from '@razz-team/ui-web';
import { BrightyTextLogo } from '../../Logo/BrightyTextLogo';
import { WolfoTextLogo } from '../../Logo/WolfoTextLogo';
import { Link } from '@tanstack/react-router';

interface AuthHeaderProps {
  currentChallenge: ApiAuthChallengeRequired | undefined;
  signup: boolean;
  usernameLabel: string;
}

const Subtitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  '& a': {
    color: 'inherit',
  },
});

export function AuthHeader({ currentChallenge, signup, usernameLabel }: AuthHeaderProps) {
  const { t } = useTranslation();

  const usernameText = usernameLabel.toLowerCase();

  const title = useMemo(() => {
    if (__NODA__) {
      return (
        <Typography variant="h2" textAlign="center">
          {t(signup ? 'Welcome To Online Banking Onboarding' : 'Welcome To Online Banking')}
        </Typography>
      );
    }

    if (__BRIGHTY__) {
      return <BrightyTextLogo />;
    }

    if (__WOLFO__) {
      return <WolfoTextLogo />;
    }

    return (
      <Typography variant="h2" textAlign="center">
        {t('Welcome to {{productName}}')}
      </Typography>
    );
  }, [signup, t]);

  const subtitle = useMemo(() => {
    if (!currentChallenge) {
      if (signup) {
        return (
          <>
            <span>
              {t('Start sign up process by entering your {{usernameText}} or', {
                usernameText,
              })}
            </span>
            &nbsp;
            <Link to="/auth">{t('sign in')}</Link>
          </>
        );
      }

      return (
        <>
          <span>{t('Sign into your account or')}</span>&nbsp;
          <Link to="/auth" search={{ signup: true }}>
            {t('create a new one')}
          </Link>
          &nbsp;
          <span>{t(`if you're new here`)}</span>
        </>
      );
    }

    if (currentChallenge?.otp) {
      if (currentChallenge.otp.destination) {
        return t(
          `We've sent you an OTP code to {{destination}}. Enter it here to verify your {{usernameText}}`,
          {
            destination: currentChallenge?.otp?.destination,
            usernameText,
          },
        );
      }

      return t(`Enter OTP code here to verify your {{usernameText}}`, {
        usernameText,
      });
    }

    switch (currentChallenge?.challenge) {
      case 'PASSWORD':
        return t('Please enter your password to continue');
      case 'NEW_PASSWORD':
        return t('You need to create a new password to continue');
      default:
        return null;
    }
  }, [currentChallenge, usernameText, signup, t]);

  return (
    <>
      {title}
      {subtitle ? <Subtitle variant="subtitle1">{subtitle}</Subtitle> : null}
    </>
  );
}
